import { apiFetch } from "@/lib/api-client";
import type { MetricValues } from "@/sync/models/post-target";
import type { SocialPlatform } from "@/sync/models/social-account";

export type Metrics = {
	impressions: number;
	reach: number;
	views: number;
	likes: number;
	comments: number;
	shares: number;
	saves: number;
	clicks: number;
};

export type Totals = Metrics & {
	posts: number;
	engagements: number;
	engagementRate: number | null;
};

export type AnalyticsOverview = {
	timeZone: string;
	range: { from: string; to: string };
	totals: Totals;
	previous: Totals;
	trend: { date: string; posts: number; views: number; reach: number; engagements: number }[];
	platforms: {
		platform: SocialPlatform;
		posts: number;
		metrics: Metrics;
		engagements: number;
		engagementRate: number | null;
	}[];
	topPosts: {
		postTargetId: string;
		postId: string;
		platform: SocialPlatform;
		caption: string;
		publishedAt: string;
		url: string | null;
		metrics: Metrics;
		engagements: number;
		engagementRate: number | null;
		artifactId: string | null;
	}[];
	bestTimes: { weekday: number; hour: number; posts: number; engagementRate: number }[];
};

export const getAnalyticsOverview = (projectId: string, from: string, to: string) =>
	apiFetch<AnalyticsOverview>(
		`/analytics/overview?projectId=${projectId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
	);

/** Queues a forced content sync (import, metrics, comments, followers); results land via sync. */
export const syncAnalytics = (projectId: string) =>
	apiFetch<{ queued: true }>("/analytics/sync", {
		method: "POST",
		body: JSON.stringify({ projectId }),
	});

export type MetricSnapshot = {
	capturedAt: string;
	impressions: number | null;
	reach: number | null;
	views: number | null;
	likes: number | null;
	comments: number | null;
	shares: number | null;
	saves: number | null;
	clicks: number | null;
	engagementRate: number | null;
};

export type PostMetrics = {
	targets: {
		postTargetId: string;
		socialAccountId: string;
		platform: SocialPlatform;
		url: string | null;
		latestMetrics: MetricValues | null;
		metricsUpdatedAt: string | null;
		/** Oldest first, at most 500 per target. */
		snapshots: MetricSnapshot[];
	}[];
};

/** SWR key for `getPostMetrics`; revalidate it after a refresh. */
export const postMetricsKey = (postId: string) => `/posts/${postId}/metrics`;

export const getPostMetrics = (postId: string) => apiFetch<PostMetrics>(postMetricsKey(postId));

/** Re-reads one post's metrics and comments now (inline). */
export const refreshPost = (postId: string) =>
	apiFetch<{ metrics: number; comments: number }>(`/posts/${postId}/refresh`, { method: "POST" });
